<template>
  <div class="min-h-screen bg-white dark:bg-gradient-to-br dark:from-[#0c1427] dark:to-[#1a2332]">
    <!-- Stats Cards Section with glass morphism effect -->
    <div class="px-4 sm:px-6 lg:px-8 pt-6 pb-8">
      <!-- Dashboard Charts Section -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <!-- Total Revenue Card -->
          <div class="bg-white dark:bg-white/10 backdrop-blur-[10px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background: linear-gradient(135deg, #10b981, #059669);">
                <i class="material-symbols-outlined text-white !text-[24px]">trending_up</i>
              </div>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">{{ directT('crm.customers.totalRevenue') || 'סך הכנסות' }}</h3>
            <p class="text-2xl font-bold text-gray-900 dark:text-white mb-2">{{ formatCurrency(totalRevenue) }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('crm.customers.last30Days') || 'הימים האחרונים 30' }}</p>
          </div>

          <!-- New Customers Card -->
          <div class="bg-white dark:bg-white/10 backdrop-blur-[10px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background: linear-gradient(135deg, #8b5cf6, #7c3aed);">
                <i class="material-symbols-outlined text-white !text-[24px]">group_add</i>
              </div>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">{{ directT('crm.customers.newCustomers') || 'לקוחות חדשים' }}</h3>
            <p class="text-2xl font-bold text-gray-900 dark:text-white mb-2">{{ newCustomersCount }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('crm.customers.last30Days') || 'הימים האחרונים 30' }}</p>
          </div>

          <!-- Active Customers Card -->
          <div class="bg-white dark:bg-white/10 backdrop-blur-[10px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background: linear-gradient(135deg, #3b82f6, #1d4ed8);">
                <i class="material-symbols-outlined text-white !text-[24px]">groups</i>
              </div>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">{{ directT('crm.customers.activeCustomers') || 'לקוחות פעילים' }}</h3>
            <p class="text-2xl font-bold text-gray-900 dark:text-white mb-2">{{ activeCustomersCount }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('crm.customers.last30Days') || 'הימים האחרונים 30' }}</p>
          </div>

          <!-- Total Customers Card -->
          <div class="bg-white dark:bg-white/10 backdrop-blur-[10px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background: linear-gradient(135deg, #f97316, #ea580c);">
                <i class="material-symbols-outlined text-white !text-[24px]">groups</i>
              </div>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">{{ directT('crm.customers.totalCustomers') || 'סך הלקוחות' }}</h3>
            <p class="text-2xl font-bold text-gray-900 dark:text-white mb-2">{{ totalCustomers }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('crm.customers.allTime') || 'כל הזמנים' }}</p>
          </div>
      </div>
    </div>

    <!-- Main Content Card with modern design -->
    <div class="px-4 sm:px-6 lg:px-8 pb-8">
      <div class="bg-white dark:bg-white/10 backdrop-blur-[20px] shadow-[0_20px_40px_0_rgba(0,0,0,0.1)] rounded-3xl overflow-hidden border border-gray-100 dark:border-white/10">
        <!-- Header Section -->
        <div class="px-6 lg:px-8 py-6 border-b border-black/[0.06] dark:border-white/[0.06]">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <!-- Search Bar with modern design -->
            <div class="flex items-center gap-3 w-full sm:w-auto">
              <div class="relative w-full sm:w-auto">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i class="material-symbols-outlined !text-[20px] text-[#8e8e93]">search</i>
                </div>
                <input
                  type="text"
                  :placeholder="directT('crm.customers.searchByNameOrEmail') || 'חיפוש לקוחות...'"
                  v-model="searchQuery"
                  @input="handleSearch"
                  class="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 pl-11 pr-4 h-11 text-[15px] rounded-full w-full sm:w-[320px] text-black dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-blue-500/30 transition-all shadow-md hover:shadow-lg focus:shadow-xl"
                />
              </div>

              <!-- Filter Button -->
              <button
                @click="showFilterModal = true"
                class="h-11 px-5 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 flex items-center gap-2 hover:shadow-lg transition-all shadow-md"
                :class="{ 'ring-2 ring-blue-500': hasActiveFilters }"
              >
                <i class="material-symbols-outlined !text-[20px] text-[#8e8e93] dark:text-gray-400">filter_list</i>
                <span class="text-[15px] font-medium text-[#1d1d1f] dark:text-white">{{ directT('crm.customers.filter') || 'סנן' }}</span>
                <span v-if="activeFilterCount > 0" class="ml-1 px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full">
                  {{ activeFilterCount }}
                </span>
              </button>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 w-full sm:w-auto">
              <!-- Bulk Assign Button -->
              <button
                v-if="selectedCustomers.length > 0"
                type="button"
                @click="showBulkAssignModal"
                style="background: #007AFF; color: white; padding: 12px 20px; font-size: 15px; font-weight: 500;"
                class="rounded-full flex items-center gap-2 hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <i class="material-symbols-outlined !text-[20px]">person_add</i>
                <span>{{ directT('crm.customers.assignTo') || 'הקצה ל' }} ({{ selectedCustomers.length }})</span>
              </button>

              <!-- Add New Customer Button -->
              <button
                type="button"
                @click="addNewCustomer"
                style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 20px; font-size: 15px; font-weight: 500;"
                class="rounded-full flex items-center gap-2 hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <i class="material-symbols-outlined !text-[20px]">add</i>
                <span>{{ directT('crm.customers.createCustomer') || 'לקוח חדש' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <div class="overflow-x-auto">
          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center items-center py-20">
            <div class="relative">
              <div class="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
              <div class="w-16 h-16 rounded-full border-4 border-t-blue-500 animate-spin absolute top-0"></div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="clients.length === 0" class="flex flex-col items-center justify-center py-20">
            <div class="w-24 h-24 rounded-full bg-[#f2f2f7] dark:bg-white/5 flex items-center justify-center mb-4">
              <i class="material-symbols-outlined !text-[48px] text-[#8e8e93] dark:text-gray-400">group</i> 
            </div>
            <p class="text-lg font-medium text-[#1d1d1f] dark:text-white">{{ directT('crm.customers.noCustomers') || 'אין לקוחות' }}</p>
            <p class="text-sm mt-1 text-[#8e8e93] dark:text-gray-400">{{ directT('crm.customers.startByAdding') || 'התחל על ידי הוספת הלקוח הראשון שלך' }}</p>
          </div>
          
          <!-- Table -->
          <table v-else class="w-full">
            <thead>
              <tr style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);">
                    <th class="px-3 py-4 text-left" style="width: 40px; max-width: 40px;">
                      <div class="flex items-center justify-center">
                        <input 
                          type="checkbox" 
                          class="w-4 h-4 rounded border border-gray-300 text-purple-600 focus:ring-1 focus:ring-purple-400 transition-all duration-200" 
                          v-model="selectAll" 
                          @change="toggleSelectAll" 
                        />
                      </div>
                    </th>
                    <th class="px-3 py-4 text-center text-white font-medium text-sm tracking-wide">
                      {{ directT('dashboard.customer') || 'לקוח' }}
                    </th>
                    <th class="px-3 py-4 text-center text-white font-medium text-sm tracking-wide">
                      {{ directT('common.email') || 'אימייל' }}
                    </th>
                    <th class="px-3 py-4 text-center text-white font-medium text-sm tracking-wide">
                      {{ directT('common.phone') || 'טלפון' }}
                    </th>
                    <th class="px-3 py-4 text-center text-white font-medium text-sm tracking-wide">
                      {{ directT('dashboard.crm.source') || 'מקור' }}
                    </th>
                    <th class="px-3 py-4 text-center text-white font-medium text-sm tracking-wide">
                      {{ directT('dashboard.crm.action') || 'פעולות' }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="client in displayedCustomers" :key="client.id" 
                      class="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group cursor-pointer"
                      @click="viewCustomer(client)">
                    <td class="px-3 py-4" style="width: 40px; max-width: 40px;" @click.stop>
                      <div class="flex items-center justify-center">
                        <input 
                          type="checkbox" 
                          class="w-4 h-4 rounded border border-gray-300 text-purple-600 focus:ring-1 focus:ring-purple-400 transition-all duration-200" 
                          v-model="selectedCustomers" 
                          :value="client.id" 
                        />
                      </div>
                    </td>
                    <td class="px-3 py-4 text-center">
                      <span class="font-medium text-gray-900 dark:text-gray-100 text-sm">{{ client.first_name }} {{ client.last_name }}</span>
                    </td>
                    <td class="px-3 py-4 text-center">
                      <span class="text-gray-600 dark:text-gray-100 text-sm">{{ client.email }}</span>
                    </td>
                    <td class="px-3 py-4 text-center">
                      <span class="text-gray-600 dark:text-gray-100 text-sm">{{ client.phone }}</span>
                    </td>
                    <td class="px-3 py-4 text-center">
                      <span class="text-gray-600 dark:text-gray-100 text-sm">{{ client.source || 'Direct' }}</span>
                    </td>
                    <td class="px-3 py-4 text-center" @click.stop>
                      <div class="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          class="w-6 h-6 rounded-md text-green-600 dark:text-white hover:text-green-700 dark:hover:text-gray-300 hover:bg-green-50 dark:hover:bg-gray-700 transition-all duration-200 flex items-center justify-center"
                          @click="editCustomer(client)"
                        >
                          <i class="material-symbols-outlined !text-[14px]">edit</i>
                        </button>
                        <button
                          type="button"
                          class="w-6 h-6 rounded-md text-red-600 dark:text-white hover:text-red-700 dark:hover:text-gray-300 hover:bg-red-50 dark:hover:bg-gray-700 transition-all duration-200 flex items-center justify-center"
                          @click="deleteClient(client.id)"
                        >
                          <i class="material-symbols-outlined !text-[14px]">delete</i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="border-t border-white/30 dark:border-gray-600 bg-white/80 dark:bg-gray-700/50 backdrop-blur-md">
              <Pagination
                :items="itemsPerPage"
                :total="filteredClients.length"
                :current-page="currentPage"
                @page-change="handlePageChange"
                class="px-6 py-4"
              />
            </div>
          </div>
        </div>
      </div>
   

  <!-- Filter Modal -->
  <div v-if="showFilterModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeFilterModal"></div>
    <div class="relative transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-2xl">

      <!-- Header -->
      <div class="flex justify-between items-start mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center shadow-lg">
            <i class="material-symbols-outlined text-white !text-[24px]">filter_list</i>
          </div>
          <div>
            <h3 class="text-2xl font-bold bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
              {{ directT('crm.customers.filter') || 'סינון לקוחות' }}
            </h3>
            <p class="text-sm text-[#8e8e93] dark:text-gray-400 mt-1">
              {{ directT('crm.customers.filterByDifferentCriteria') || 'סנן לקוחות לפי קריטריונים שונים' }}
            </p>
          </div>
        </div>
        <button
          @click="closeFilterModal"
          class="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110 hover:rotate-90"
        >
          <i class="material-symbols-outlined text-[#8e8e93] dark:text-gray-400">close</i>
        </button>
      </div>

      <!-- Filter Content -->
      <div class="space-y-6">
        <!-- Status Filter -->
        <div>
          <h4 class="text-lg font-semibold mb-3 text-[#1d1d1f] dark:text-white">{{ directT('crm.customers.status') || 'סטטוס' }}</h4>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="status in customerStatuses"
              :key="status.id"
              @click="toggleFilter('status', status.statusname)"
              class="px-3 py-1 rounded-full text-sm font-medium transition-all"
              :class="filters.status.includes(status.statusname) ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20'"
            >
              {{ status.statusname }}
            </button>
          </div>
        </div>

        <!-- Source Filter -->
        <div>
          <h4 class="text-lg font-semibold mb-3 text-[#1d1d1f] dark:text-white">{{ directT('crm.customers.source') || 'מקור' }}</h4>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="source in customerSources"
              :key="source.name"
              @click="toggleFilter('source', source.name)"
              class="px-3 py-1 rounded-full text-sm font-medium transition-all"
              :class="filters.source.includes(source.name) ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20'"
            >
              {{ source.name }}
            </button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 mt-8">
        <button
          @click="clearAllFilters"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white"
        >
          {{ directT('crm.customers.clearAll') || 'מחק הכל' }}
        </button>
        <button
          @click="applyFilters"
          class="flex-1 py-3 rounded-2xl font-medium transition-all bg-[#007AFF] text-white"
        >
          {{ directT('crm.customers.applyFilters') || 'החל סינון' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Add New Customer Modal -->
  <div v-if="showAddModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeModal"></div>
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl z-10 relative max-h-[90vh] overflow-hidden mt-[125px] sm:mt-0">
      <!-- Modal Header -->
      <div class="p-6 border-b border-gray-200" style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <i class="material-symbols-outlined text-white !text-[20px]">person_add</i>
            </div>
            <div>
              <h3 class="text-xl font-bold text-white">{{ directT('crm.customers.modal.title') }}</h3>
            </div>
          </div>
          <button 
            @click="closeModal" 
            class="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
          >
            <i class="material-symbols-outlined !text-[20px]">close</i>
          </button>
        </div>
      </div>
      
      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-200px)] bg-white">
        <div class="space-y-5">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ directT('crm.customers.modal.firstName') }}
              </label>
              <input
                v-model="newCustomer.first_name"
                type="text"
                :placeholder="directT('crm.customers.modal.firstNamePlaceholder')"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ directT('crm.customers.modal.lastName') }}
              </label>
              <input
                v-model="newCustomer.last_name"
                type="text"
                :placeholder="directT('crm.customers.modal.lastNamePlaceholder')"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ directT('crm.customers.modal.email') }}
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="material-symbols-outlined text-gray-400 !text-[20px]">mail</i>
              </div>
              <input
                v-model="newCustomer.email"
                type="email"
                :placeholder="directT('crm.customers.modal.emailPlaceholder')"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white pl-10 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ directT('crm.customers.modal.phone') }}
              </label>
              <input
                v-model="newCustomer.phone"
                type="tel"
                :placeholder="directT('crm.customers.modal.phonePlaceholder')"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ directT('crm.customers.modal.company') }}
              </label>
              <input
                v-model="newCustomer.company"
                type="text"
                :placeholder="directT('crm.customers.modal.companyPlaceholder')"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ directT('crm.customers.modal.source') }}
              </label>
              <select
                v-model="newCustomer.source"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer shadow-sm"
              >
                <option value="">{{ directT('crm.customers.modal.selectSource') }}</option>
                <option value="Website">{{ directT('crm.customers.modal.sources.website') }}</option>
                <option value="Social Media">{{ directT('crm.customers.modal.sources.socialMedia') }}</option>
                <option value="Email Campaign">{{ directT('crm.customers.modal.sources.emailCampaign') }}</option>
                <option value="Direct">{{ directT('crm.customers.modal.sources.direct') }}</option>
                <option value="Referral">{{ directT('crm.customers.modal.sources.referral') }}</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ directT('crm.customers.modal.status') }}
              </label>
              <select
                v-model="newCustomer.status"
                class="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer shadow-sm"
              >
                <option value="">{{ directT('crm.customers.modal.selectStatus') }}</option>
                <option 
                  v-for="status in customerStatuses" 
                  :key="status.id" 
                  :value="status.statusname"
                >
                  {{ status.statusname }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ directT('crm.customers.modal.notes') }}
            </label>
            <textarea
              v-model="newCustomer.notes"
              rows="4"
              :placeholder="directT('crm.customers.modal.notesPlaceholder')"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none shadow-sm"
            ></textarea>
          </div>
        </div>
      </div>
        
      <!-- Modal Footer -->
      <div class="border-t border-gray-200 bg-white p-6">
        <div class="flex justify-between gap-3">
          <button 
            type="button" 
            @click="closeModal"
            class="px-6 py-3 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90"
            style="background-color: #ef4444;"
          >
            {{ directT('crm.customers.modal.cancel') }}
          </button>
          <button 
            @click="saveCustomer"
            :disabled="saving"
            class="px-6 py-3 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
            style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);"
          >
            <span v-if="saving" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            {{ saving ? directT('crm.customers.modal.saving') : directT('crm.customers.modal.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="cancelDelete"></div>
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl w-full max-w-md z-10 relative overflow-hidden">
      <!-- Modal Header -->
      <div class="bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] dark:from-[#DC2626] dark:to-[#E11D48] p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <i class="ri-delete-bin-line text-white text-2xl"></i>
            </div>
            <div>
              <h3 class="text-xl font-bold text-white">{{ directT('crm.customers.confirmDeletion') || 'Confirm Deletion' }}</h3>
              <p class="text-red-100">{{ directT('crm.customers.cannotBeUndone') || 'This action cannot be undone' }}</p>
            </div>
          </div>
        <button 
          @click="cancelDelete" 
            class="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center text-white transition-all duration-200"
        >
            <i class="ri-close-line text-xl"></i>
        </button>
        </div>
      </div>
      
      <!-- Modal Body -->
      <div class="p-6">
        <div class="p-5 bg-gradient-to-br from-[#FEF2F2] to-[#FEE2E2] dark:from-[#1a0f0f] dark:to-[#2a1a1a] rounded-2xl border border-[#FF3B30]/10 mb-6">
          <p class="text-[#1a1a1a] dark:text-white font-medium">
            {{ directT('crm.customers.deleteConfirmation') }}
          </p>
      </div>
      
        <div class="flex gap-3">
        <button 
          type="button" 
          @click="cancelDelete"
            class="flex-1 px-4 py-3 bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] dark:text-[#40A9FF] font-medium rounded-2xl transition-all duration-200 hover:bg-[#E6F3FF] dark:hover:bg-[#1a2642] border border-[#007AFF]/20"
        >
          {{ directT('crm.customers.cancel') || 'Cancel' }}
        </button>
        <button 
          type="button" 
          @click="confirmDelete"
          :disabled="deleting"
            class="flex-1 px-4 py-3 bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#DC2626] hover:to-[#E11D48] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
        >
            <span v-if="deleting" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <i v-else class="ri-delete-bin-line text-lg"></i>
          {{ deleting ? directT('crm.customers.deleting') : directT('crm.customers.deleteCustomer') }}
        </button>
        </div>
      </div>
    </div>
  </div>

  <!-- View Customer Modal -->
  <div v-if="showViewModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeViewModal"></div>
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl w-full max-w-4xl z-10 relative max-h-[90vh] overflow-hidden">
      <!-- Modal Header -->
      <div class="bg-gradient-to-r from-[#007AFF] to-[#5856D6] dark:from-[#0056CC] dark:to-[#4338CA] p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <i class="ri-user-line text-white text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold text-white">{{ directT('crm.customers.customerDetails') || 'Customer Details' }}</h3>
              <p class="text-blue-100">{{ directT('crm.customers.viewCustomerInformation') || 'View customer information' }}</p>
            </div>
          </div>
        <button 
          @click="closeViewModal" 
            class="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center text-white transition-all duration-200"
        >
            <i class="ri-close-line text-xl"></i>
        </button>
        </div>
      </div>
      
      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Customer Info Card -->
          <div class="p-6 bg-gradient-to-br from-[#F0F7FF] to-[#E6F3FF] dark:from-[#0c1427] dark:to-[#1a2642] rounded-3xl border border-[#007AFF]/10">
            <h4 class="text-xl font-bold text-[#1a1a1a] dark:text-white mb-6 flex items-center">
              <i class="ri-user-line text-[#007AFF] mr-3 text-2xl"></i>
              {{ directT('crm.customers.personalInformation') || 'Personal Information' }}
            </h4>
      
      <div class="space-y-4">
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.fullName') }}</p>
                <p class="text-lg font-medium text-[#1a1a1a] dark:text-white">{{ selectedCustomer?.first_name }} {{ selectedCustomer?.last_name }}</p>
          </div>
          
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.email') }}</p>
                <p class="text-[#1a1a1a] dark:text-white break-words">{{ selectedCustomer?.email || 'N/A' }}</p>
          </div>
          
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.phone') }}</p>
                <p class="text-[#1a1a1a] dark:text-white">{{ selectedCustomer?.phone || 'N/A' }}</p>
          </div>
          
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.status') }}</p>
                <span class="px-3 py-1 rounded-xl font-medium text-sm" :class="getStatusClass(selectedCustomer?.status)">
                {{ selectedCustomer?.status }}
              </span>
          </div>
            </div>
          </div>
          
          <!-- Address & Business Info Card -->
          <div class="p-6 bg-gradient-to-br from-[#F0F9F5] to-[#E6F7ED] dark:from-[#0a1a0f] dark:to-[#1a2e1a] rounded-3xl border border-[#34C759]/10">
            <h4 class="text-xl font-bold text-[#1a1a1a] dark:text-white mb-6 flex items-center">
              <i class="ri-map-pin-line text-[#34C759] mr-3 text-2xl"></i>
              {{ directT('crm.customers.addressAndBusiness') || 'Address & Business' }}
            </h4>
            
            <div class="space-y-4">
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.address') }}</p>
                <p class="text-[#1a1a1a] dark:text-white">{{ selectedCustomer?.address || 'N/A' }}</p>
          </div>
          
              <div class="grid grid-cols-2 gap-4">
          <div>
                  <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.city') }}</p>
                  <p class="text-[#1a1a1a] dark:text-white">{{ selectedCustomer?.city || 'N/A' }}</p>
          </div>
          
          <div>
                  <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.country') }}</p>
                  <p class="text-[#1a1a1a] dark:text-white">{{ selectedCustomer?.country || 'N/A' }}</p>
                </div>
          </div>
          
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.receivedAmount') }}</p>
                <p class="text-xl font-bold text-[#34C759]">{{ formatCurrency(selectedCustomer?.received) }}</p>
          </div>
          
          <div>
                <p class="text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-1">{{ directT('crm.customers.source') }}</p>
                <p class="text-[#1a1a1a] dark:text-white">
              {{ selectedCustomer?.shopify_id ? directT('crm.customers.shopify') : 
                 selectedCustomer?.woocommerce_id ? directT('crm.customers.wooCommerce') : 
                 directT('crm.customers.manual') }}
            </p>
          </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Modal Footer -->
      <div class="border-t border-[#E5E7EB] dark:border-[#374151] bg-[#F8F9FA] dark:bg-[#0c0c0c] p-6">
        <div class="flex justify-end">
        <button 
          type="button" 
          @click="closeViewModal"
            class="px-6 py-3 bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] dark:text-[#40A9FF] font-medium rounded-2xl transition-all duration-200 hover:bg-[#E6F3FF] dark:hover:bg-[#1a2642] border border-[#007AFF]/20"
        >
          {{ directT('crm.customers.close') || 'Close' }}
        </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Customer Modal -->
  <div v-if="showEditModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="cancelEdit"></div>
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl w-full max-w-4xl z-10 relative max-h-[90vh] overflow-hidden">
      <!-- Modal Header -->
      <div class="bg-gradient-to-r from-[#007AFF] to-[#5856D6] dark:from-[#0056CC] dark:to-[#4338CA] p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <i class="ri-edit-line text-white text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold text-white">{{ directT('crm.customers.editCustomer') || 'Edit Customer' }}</h3>
              <p class="text-blue-100">{{ directT('crm.customers.updateCustomerInformation') || 'Update customer information' }}</p>
            </div>
          </div>
        <button 
          @click="cancelEdit" 
            class="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center text-white transition-all duration-200"
        >
            <i class="ri-close-line text-xl"></i>
        </button>
        </div>
      </div>
      
      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Personal Information Section -->
          <div class="p-6 bg-gradient-to-br from-[#F0F7FF] to-[#E6F3FF] dark:from-[#0c1427] dark:to-[#1a2642] rounded-3xl border border-[#007AFF]/10">
            <h4 class="text-xl font-bold text-[#1a1a1a] dark:text-white mb-6 flex items-center">
              <i class="ri-user-line text-[#007AFF] mr-3 text-2xl"></i>
              {{ directT('crm.customers.personalInformation') || 'Personal Information' }}
            </h4>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  First Name <span class="text-[#FF3B30]">*</span>
                </label>
            <input 
              v-model="editingCustomer.first_name" 
                  type="text"
              required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10"
                  placeholder="Enter first name"
            />
          </div>
          
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  Last Name <span class="text-[#FF3B30]">*</span>
                </label>
            <input 
              v-model="editingCustomer.last_name" 
                  type="text"
              required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10"
                  placeholder="Enter last name"
            />
              </div>
            </div>
          </div>
          
          <!-- Contact Information Section -->
          <div class="p-6 bg-gradient-to-br from-[#F0F9F5] to-[#E6F7ED] dark:from-[#0a1a0f] dark:to-[#1a2e1a] rounded-3xl border border-[#34C759]/10">
            <h4 class="text-xl font-bold text-[#1a1a1a] dark:text-white mb-6 flex items-center">
              <i class="ri-phone-line text-[#34C759] mr-3 text-2xl"></i>
              Contact Information
            </h4>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  Email Address <span class="text-[#FF3B30]">*</span>
                </label>
            <input 
              v-model="editingCustomer.email" 
                  type="email"
              required
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#34C759] focus:ring-4 focus:ring-[#34C759]/10"
                  placeholder="customer@example.com"
            />
          </div>
          
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  Phone Number
                </label>
            <input 
              v-model="editingCustomer.phone" 
                  type="tel"
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#34C759] focus:ring-4 focus:ring-[#34C759]/10"
                  placeholder="+972-50-123-4567"
            />
              </div>
            </div>
          </div>
          
          <!-- Business Information Section -->
          <div class="p-6 bg-gradient-to-br from-[#F8F0FF] to-[#F3E8FF] dark:from-[#1a0d1a] dark:to-[#2a1a2a] rounded-3xl border border-[#5856D6]/10">
            <h4 class="text-xl font-bold text-[#1a1a1a] dark:text-white mb-6 flex items-center">
              <i class="ri-building-line text-[#5856D6] mr-3 text-2xl"></i>
              Business Information
            </h4>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  Customer Status
                </label>
                <select
                  v-model="editingCustomer.status"
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none cursor-pointer transition-all focus:border-[#5856D6] focus:ring-4 focus:ring-[#5856D6]/10"
                >
                  <option value="New">New Customer</option>
                  <option value="Active">Active Customer</option>
                  <option value="Inactive">Inactive Customer</option>
                  <option value="Pending">Pending Approval</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  Total Revenue
                </label>
                <div class="relative">
                  <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[#5856D6] font-bold">₪</span>
            <input 
                  v-model="editingCustomer.received"
                  type="number"
                  min="0"
                  step="0.01"
                    class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] pl-10 pr-4 outline-none transition-all focus:border-[#5856D6] focus:ring-4 focus:ring-[#5856D6]/10"
                  placeholder="0.00"
            />
                </div>
              </div>
            </div>
          </div>
          
          <!-- Address Information Section -->
          <div class="p-6 bg-gradient-to-br from-[#FFF7E6] to-[#FEF3C7] dark:from-[#1a1406] dark:to-[#2a2306] rounded-3xl border border-[#FF9500]/10">
            <h4 class="text-xl font-bold text-[#1a1a1a] dark:text-white mb-6 flex items-center">
              <i class="ri-map-pin-line text-[#FF9500] mr-3 text-2xl"></i>
              {{ directT('crm.customers.addressInformation') || 'Address Information' }}
            </h4>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                Street Address
              </label>
            <input 
                v-model="editingCustomer.address"
              type="text" 
                  class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                placeholder="רחוב דיזנגוף 123, תל אביב"
            />
          </div>
          
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  City
                </label>
            <input 
                  v-model="editingCustomer.city"
              type="text" 
                    class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                  placeholder="Tel Aviv"
            />
          </div>
          
                <div>
                  <label class="block text-sm font-semibold text-[#666666] dark:text-[#cccccc] mb-2">
                  Country
                </label>
            <input 
              v-model="editingCustomer.country" 
                  type="text"
                    class="w-full h-12 rounded-2xl text-[#1a1a1a] dark:text-white border-2 border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] px-4 outline-none transition-all focus:border-[#FF9500] focus:ring-4 focus:ring-[#FF9500]/10"
                  placeholder="Israel"
            />
          </div>
            </div>
          </div>
          </div>
        </div>
        </div>
        
      <!-- Modal Footer -->
      <div class="border-t border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c] p-6">
        <div class="flex justify-end gap-4">
          <button 
            type="button" 
            @click="cancelEdit"
            class="px-6 py-3 bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] dark:text-[#40A9FF] font-medium rounded-2xl transition-all duration-200 hover:bg-[#E6F3FF] dark:hover:bg-[#1a2642] border border-[#007AFF]/20"
          >
            {{ directT('crm.customers.cancel') || 'Cancel' }}
          </button>
          <button 
          @click="saveEdit"
            :disabled="saving"
            class="px-6 py-3 bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#0056CC] hover:to-[#4338CA] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center gap-2 min-w-[140px] justify-center"
          >
          <span v-if="saving" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <i v-else class="ri-save-line text-lg"></i>
          {{ saving ? (directT('crm.customers.updating') || 'Updating...') : (directT('crm.customers.updateCustomer') || 'Update Customer') }}
          </button>
        </div>
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import Pagination from "@/components/Common/Pagination.vue";
import { createToast } from "@/utils/toast";
import { useRouter } from "vue-router";
import stateStore from "@/utils/store";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// Interface for custom fields
interface CustomField {
  id?: string;
  field: string;
  isrequired: boolean;
  organizationid?: string;
}

export default defineComponent({
  name: "CustomersList",
  components: {
    Pagination,
  },
  setup() {
    const router = useRouter();
    const clients = ref<any[]>([]);
    const loading = ref(true);
    const isActive = ref(false);
    const searchQuery = ref("");
    const statusFilter = ref("All");
    const statusOptions = ref(["All", "New", "Active", "Inactive"]);
    const organizationId = ref<string | null>(null);
    const selectedClients = ref<string[]>([]);
    const selectedCustomers = ref<string[]>([]);
    const selectAll = ref(false);
    
    // Pagination
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    
    // New variables for dashboard
    const timeFilter = ref(360);
    const totalCustomers = computed(() => clients.value.length);
    const newCustomers = computed(() => clients.value.filter(c => c.status?.toLowerCase() === 'new').length);
    const newCustomersCount = computed(() => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return clients.value.filter(c => 
        c.status?.toLowerCase() === 'new' && 
        new Date(c.created_at) >= thirtyDaysAgo
      ).length;
    });
    const activeCustomersCount = computed(() => clients.value.filter(c => c.status?.toLowerCase() === 'active').length);
    const totalRevenue = computed(() => {
      return clients.value.reduce((sum, client) => {
        const receivedAmount = parseFloat(client.received) || 0;
        return sum + receivedAmount;
      }, 0);
    });
    
    // New Customer Modal
    const showAddModal = ref(false);
    const saving = ref(false);
    const newCustomer = ref({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: 'Israel',
      status: 'New',
      received: 0,
      organization_id: '',
      company: '',
      source: '',
      notes: ''
    });

    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleting = ref(false);
    const clientToDelete = ref<string | null>(null);

    // View Customer modal
    const showViewModal = ref(false);
    const selectedCustomer = ref<any | null>(null);
    
    // Edit Customer modal
    const showEditModal = ref(false);
    const editingCustomer = ref<any>({});

    // Load More functionality
    const loadingMore = ref(false);
    const displayLimit = ref(10);
    const totalCustomersCount = ref(0);

    // הוספת משתנים לשדות מותאמים אישית
    const customFields = ref<CustomField[]>([]);
    const loadingCustomFields = ref(false);
    const customFieldValues = ref<Record<string, string>>({});

    // Filter Modal
    const showFilterModal = ref(false);
    const filters = ref({
      status: [] as string[],
      source: [] as string[]
    });

    // Customer statuses - loaded from database
    const customerStatuses = ref<Array<{id: string, statusname: string, color: string}>>([]);

    // Customer sources for filter
    const customerSources = ref([
      { name: 'Website' },
      { name: 'Social Media' },
      { name: 'Email Campaign' },
      { name: 'Direct' },
      { name: 'Referral' }
    ]);

    // Computed properties for filter
    const hasActiveFilters = computed(() => {
      return filters.value.status.length > 0 || filters.value.source.length > 0;
    });

    const activeFilterCount = computed(() => {
      return filters.value.status.length + filters.value.source.length;
    });

    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Toggle dropdown
    const toggleActive = () => {
      isActive.value = !isActive.value;
    };

    // Set status filter
    const setStatusFilter = (status: string) => {
      statusFilter.value = status;
      isActive.value = false;
    };

    // Format date for display
    const formatDate = (dateString: string) => {
      if (!dateString) return directT('crm.customers.notAvailable') || 'Not available';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    };

    // Get customer avatar
    const getCustomerAvatar = (client: any) => {
      const name = `${client.first_name} ${client.last_name}`;
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=40`;
    };

    // Get customer handle
    const getCustomerHandle = (name: string) => {
      return name.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9א-ת]/g, '');
    };

    // Get status class with custom colors from database
    const getStatusClass = (status: string) => {
      if (!status) {
        return {
          backgroundColor: 'transparent',
          color: '#64748b',
          border: '1px solid #e2e8f0'
        };
      }

      // First check if status exists in custom statuses
      const customStatus = customerStatuses.value.find(
        s => s.statusname.toLowerCase() === status?.toLowerCase()
      );
      
      if (customStatus) {
        return {
          backgroundColor: 'transparent',
          color: customStatus.color,
          border: `1px solid ${customStatus.color}30`
        };
      }
      
      // Fallback to default color mapping for legacy statuses
      switch (status.toLowerCase()) {
        case 'new':
          return {
            backgroundColor: 'transparent',
            color: '#6366f1',
            border: '1px solid #6366f130'
          };
        case 'active':
          return {
            backgroundColor: 'transparent',
            color: '#10b981',
            border: '1px solid #10b98130'
          };
        case 'inactive':
          return {
            backgroundColor: 'transparent',
            color: '#f59e0b',
            border: '1px solid #f59e0b30'
          };
        case 'pending':
          return {
            backgroundColor: 'transparent',
            color: '#ef4444',
            border: '1px solid #ef444430'
          };
        default:
          return {
            backgroundColor: 'transparent',
            color: '#64748b',
            border: '1px solid #e2e8f0'
          };
      }
    };

    // Format currency for display
    const formatCurrency = (amount: number | null) => {
      if (amount === null || amount === undefined) return '₪0.00';
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 2
      }).format(amount);
    };

    // Filter clients based on search query, status, and advanced filters
    const filteredClients = computed(() => {
      let result = clients.value;
      
      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(client => 
          (client.first_name && client.first_name.toLowerCase().includes(query)) ||
          (client.last_name && client.last_name.toLowerCase().includes(query)) ||
          (client.email && client.email.toLowerCase().includes(query)) ||
          (client.phone && client.phone.includes(query))
        );
      }
      
      // Filter by status (from dropdown)
      if (statusFilter.value !== 'All') {
        result = result.filter(client => 
          client.status && client.status.toLowerCase() === statusFilter.value.toLowerCase()
        );
      }

      // Filter by advanced status filters
      if (filters.value.status.length > 0) {
        result = result.filter(client => 
          client.status && filters.value.status.some(status => 
            status.toLowerCase() === client.status.toLowerCase()
          )
        );
      }

      // Filter by source
      if (filters.value.source.length > 0) {
        result = result.filter(client => 
          client.source && filters.value.source.includes(client.source)
        );
      }
      
      return result;
    });

    // Displayed customers with pagination
    const displayedCustomers = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return filteredClients.value.slice(start, end);
    });

    // Check if there are more customers to load
    const hasMoreCustomers = computed(() => {
      return displayedCustomers.value.length < filteredClients.value.length;
    });

    // Load more customers function
    const loadMoreCustomers = async () => {
      loadingMore.value = true;
      
      // Simulate loading delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      displayLimit.value += 10;
      loadingMore.value = false;
    };

    // Select or deselect all clients
    const selectAllClients = () => {
      if (selectAll.value) {
        selectedClients.value = filteredClients.value.map(client => client.id);
      } else {
        selectedClients.value = [];
      }
    };

    // Delete a client - Step 1: Open confirmation modal
    const deleteClient = (id: string) => {
      clientToDelete.value = id;
      showDeleteModal.value = true;
    };
    
    // Cancel delete operation
    const cancelDelete = () => {
      showDeleteModal.value = false;
      clientToDelete.value = null;
    };
    
    // Confirm and execute delete - Step 2: Execute the deletion
    const confirmDelete = async () => {
      if (!clientToDelete.value) return;
      
      deleting.value = true;
      
      try {
        const { error } = await supabase
          .from('clients')
          .delete()
          .eq('id', clientToDelete.value);
          
        if (error) throw error;
        
        // Remove from local array
        clients.value = clients.value.filter(client => client.id !== clientToDelete.value);
        createToast('Customer deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting client:', error);
        createToast('Failed to delete client. Please try again.', 'error');
      } finally {
        deleting.value = false;
        showDeleteModal.value = false;
        clientToDelete.value = null;
      }
    };

    // Add a new customer - open modal
    const addNewCustomer = () => {
      // Reset form
      newCustomer.value = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: 'Israel',
        status: 'New',
        received: 0,
        organization_id: organizationId.value || '',
        company: '',
        source: '',
        notes: ''
      };
      showAddModal.value = true;
    };
    
    // Close the modal
    const closeModal = () => {
      showAddModal.value = false;
    };
    
    // Save customer to database
    const saveCustomer = async () => {
      if (!organizationId.value) {
        createToast('Cannot save customer: Missing organization ID', 'error');
        return;
      }
      
      saving.value = true;
      
      try {
        newCustomer.value.organization_id = organizationId.value;
        
        // הוספת לקוח חדש
        const { data, error } = await supabase
          .from('clients')
          .insert([newCustomer.value])
          .select();
          
        if (error) throw error;
        
        if (data && data[0]) {
          const customerId = data[0].id;
          
          // שמירת שדות מותאמים אישית
          const requiredCustomFields = customFields.value.filter(field => field.isrequired);
          if (requiredCustomFields.length > 0) {
            // הכנת רשומות לשמירה
            const customFieldsToSave = [];
            
            for (const field of requiredCustomFields) {
              if (customFieldValues.value[field.field]) {
                customFieldsToSave.push({
                  customerid: customerId,
                  customerscustomfiledid: field.id,
                  fieldcontent: customFieldValues.value[field.field]
                });
              }
            }
            
            // שמירת שדות מותאמים אישית אם יש
            if (customFieldsToSave.length > 0) {
              const { error: customFieldsError } = await supabase
                .from('customercustomfieldscontent')
                .insert(customFieldsToSave);
                
              if (customFieldsError) {
                console.error('Error saving custom fields:', customFieldsError);
                // לא נזרוק שגיאה כדי שהלקוח עדיין יישמר
              }
            }
          }
          
          // הוספת הלקוח החדש לרשימה המקומית
          clients.value.push(data[0]);
          createToast('Customer added successfully', 'success');
          closeModal();
          
          // איפוס ערכי שדות מותאמים אישית
          Object.keys(customFieldValues.value).forEach(key => {
            customFieldValues.value[key] = '';
          });
        }
      } catch (error) {
        console.error('Error saving customer:', error);
        createToast('Failed to save customer', 'error');
      } finally {
        saving.value = false;
      }
    };

    // Fetch clients from Supabase
    const fetchClients = async () => {
      try {
        // Get current organization_id
        organizationId.value = await getCurrentOrgId();
        
        // If organizationId is still missing, try to get it from user_profiles
        if (!organizationId.value) {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: userProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', user.id)
              .single();
            
            if (profileError) {
              console.error('Error fetching user profile:', profileError);
            } else if (userProfile && userProfile.org_id) {
              organizationId.value = userProfile.org_id as string;
              console.log('Found organization ID from user profile:', organizationId.value);
            }
          }
        }
        
        if (!organizationId.value) {
          loading.value = false;
          createToast('Cannot load clients: User is not associated with an organization', 'error');
          return;
        }
        
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('organization_id', organizationId.value);
          
        if (error) throw error;
        
        
        clients.value = data || [];
      } catch (error) {
        console.error('Error fetching clients:', error);
        createToast('Failed to load clients. Please try again.', 'error');
      } finally {
        loading.value = false;
      }
    };

    // פונקציה לטעינת סטטוסים מותאמים אישית
    const fetchCustomStatuses = async () => {
      try {
        // Get organization ID if needed
        let orgId = organizationId.value;
        if (!orgId) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: userProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', user.id)
              .single();
            
            if (profileError) {
              console.error('Error fetching user profile:', profileError);
              return;
            }
            
            if (userProfile && userProfile.org_id) {
              orgId = userProfile.org_id as string;
              organizationId.value = orgId;
            }
          }
        }

        // Set default statuses first to ensure we always have something
        customerStatuses.value = [
          { id: 'new', statusname: 'New', color: '#6366f1' },
          { id: 'active', statusname: 'Active', color: '#10b981' },
          { id: 'inactive', statusname: 'Inactive', color: '#f59e0b' },
          { id: 'pending', statusname: 'Pending', color: '#ef4444' }
        ];

        if (!orgId) {
          console.warn('Cannot fetch custom statuses: No organization ID, using defaults');
          return;
        }

        console.log('🎨 Fetching custom statuses for organization:', orgId);

        const { data, error } = await supabase
          .from('customercustomstatus')
          .select('id, statusname, color')
          .eq('organizationid', orgId)
          .order('statusname');

        if (error) {
          console.error('Error fetching custom statuses:', error);
          // Fallback to default statuses
          customerStatuses.value = [
            { id: 'new', statusname: 'New', color: '#6366f1' },
            { id: 'active', statusname: 'Active', color: '#10b981' },
            { id: 'inactive', statusname: 'Inactive', color: '#f59e0b' },
            { id: 'pending', statusname: 'Pending', color: '#ef4444' }
          ];
          return;
        }

        if (data && data.length > 0) {
          customerStatuses.value = data as { id: string; statusname: string; color: string; }[];
          console.log('🎨 Loaded custom statuses:', data);
        } else {
          // No custom statuses found, use defaults
          console.log('🎨 No custom statuses found, using defaults');
          customerStatuses.value = [
            { id: 'new', statusname: 'New', color: '#6366f1' },
            { id: 'active', statusname: 'Active', color: '#10b981' },
            { id: 'inactive', statusname: 'Inactive', color: '#f59e0b' },
            { id: 'pending', statusname: 'Pending', color: '#ef4444' }
          ];
        }
      } catch (error) {
        console.error('Error in fetchCustomStatuses:', error);
        // Fallback to default statuses
        customerStatuses.value = [
          { id: 'new', statusname: 'New', color: '#6366f1' },
          { id: 'active', statusname: 'Active', color: '#10b981' },
          { id: 'inactive', statusname: 'Inactive', color: '#f59e0b' },
          { id: 'pending', statusname: 'Pending', color: '#ef4444' }
        ];
      }
    };

    // פונקציה לטעינת שדות מותאמים אישית
    const fetchCustomFields = async () => {
      try {
        // If organization ID is missing, try to get it from user_profiles
        if (!organizationId.value) {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: userProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', user.id)
              .single();
            
            if (profileError) {
              console.error('Error fetching user profile:', profileError);
              throw profileError;
            }
            
            if (userProfile && userProfile.org_id) {
              organizationId.value = userProfile.org_id as string;
              console.log('Found organization ID from user profile:', organizationId.value);
            } else {
              console.warn('No organization ID found in user profile');
              loadingCustomFields.value = false;
              return;
            }
          } else {
            console.warn('Cannot fetch custom fields: User is not logged in');
            loadingCustomFields.value = false;
            return;
          }
        }
        
        loadingCustomFields.value = true;
        
        const { data, error } = await supabase
          .from('customclientsfield')
          .select('*')
          .eq('organizationid', organizationId.value);
        
        if (error) throw error;
        
        // מיפוי הנתונים למבנה הנדרש
        customFields.value = (data || []).map((item: any) => ({
          id: item.id,
          field: item.field,
          isrequired: item.isrequired,
          organizationid: item.organizationid
        }));
        
        // איפוס ערכי השדות
        customFields.value.forEach(field => {
          customFieldValues.value[field.field] = '';
        });
        
        console.log('Custom fields loaded:', customFields.value);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        createToast('Failed to load custom fields', 'error');
      } finally {
        loadingCustomFields.value = false;
      }
    };

    // New functions for dashboard
    const applyTimeFilter = () => {
      console.log('Time filter changed to:', timeFilter.value, 'days');
    };
    
    const getStatusCount = (status: string) => {
      if (status === 'All') return clients.value.length;
      return clients.value.filter(client => client.status?.toLowerCase() === status.toLowerCase()).length;
    };

    // Close dropdown when clicking outside
    const closeDropdown = (event: MouseEvent) => {
      if (isActive.value) {
        isActive.value = false;
      }
    };

    // Add event listener for closing dropdown
    onMounted(() => {
      document.addEventListener('click', closeDropdown);
      fetchClients();
      fetchCustomFields(); // טעינת שדות מותאמים אישית
      fetchCustomStatuses(); // טעינת סטטוסים מותאמים אישית
    });

    // Watch for changes in selection
    watch(selectedClients, () => {
      selectAll.value = selectedClients.value.length === filteredClients.value.length && filteredClients.value.length > 0;
    });

    // View customer
    const viewCustomer = (client: any) => {
      router.push(`/crm/customers/${client.id}`);
    };

    // Close view modal
    const closeViewModal = () => {
      showViewModal.value = false;
    };

    // Edit customer
    const editCustomer = (client: any) => {
      router.push(`/crm/customers/${client.id}`);
    };
    
    // Cancel edit
    const cancelEdit = () => {
      showEditModal.value = false;
    };
    
    // Save edited customer
    const saveEdit = async () => {
      if (!editingCustomer.value.id) {
        createToast('Customer ID is required', 'error');
        return;
      }
      
      saving.value = true;
      
      try {
        const { data, error } = await supabase
          .from('clients')
          .update(editingCustomer.value)
          .eq('id', editingCustomer.value.id)
          .select();
          
        if (error) {
          console.error('Supabase error details:', error);
          throw error;
        }
        
        // Update client in the list
        if (data && data.length > 0) {
          const index = clients.value.findIndex(c => c.id === editingCustomer.value.id);
          if (index !== -1) {
            clients.value[index] = data[0];
          }
          createToast('Customer updated successfully!', 'success');
        }
        
        // Close modal
        showEditModal.value = false;
      } catch (error) {
        console.error('Error updating customer:', error);
        createToast(`Failed to update customer: ${error instanceof Error ? error.message : 'Access denied. Check permissions.'}`, 'error');
      } finally {
        saving.value = false;
      }
    };

    // Handle search
    const handleSearch = () => {
      currentPage.value = 1;
    };

    // Toggle select all
    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedCustomers.value = displayedCustomers.value.map(customer => customer.id);
      } else {
        selectedCustomers.value = [];
      }
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
      currentPage.value = page;
    };

    // Get customer image URL
    const getCustomerImageUrl = (client: any) => {
      const name = `${client.first_name} ${client.last_name}`;
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=32`;
    };

    // Show assign modal (placeholder)
    const showAssignModal = (client: any) => {
      console.log('Show assign modal for client:', client);
      // TODO: Implement assign modal functionality
    };

    // Show bulk assign modal (placeholder)
    const showBulkAssignModal = () => {
      console.log('Show bulk assign modal');
      // TODO: Implement bulk assign modal functionality
    };

    // Get assigned user name (placeholder)
    const getAssignedUserName = (client: any) => {
      // TODO: Implement proper user lookup
      return client.assigned_to ? 'משתמש' : 'לא מוקצה';
    };

    // Filter Functions
    const closeFilterModal = () => {
      showFilterModal.value = false;
    };

    const toggleFilter = (filterType: 'status' | 'source', value: string) => {
      const filterArray = filters.value[filterType];
      const index = filterArray.indexOf(value);
      
      if (index > -1) {
        filterArray.splice(index, 1);
      } else {
        filterArray.push(value);
      }
    };

    const clearAllFilters = () => {
      filters.value.status = [];
      filters.value.source = [];
    };

    const applyFilters = () => {
      showFilterModal.value = false;
      currentPage.value = 1; // Reset to first page when applying filters
    };

    return {
      clients,
      loading,
      isActive,
      toggleActive,
      searchQuery,
      statusFilter,
      statusOptions,
      setStatusFilter,
      formatDate,
      formatCurrency,
      filteredClients,
      selectedClients,
      selectAll,
      selectAllClients,
      deleteClient,
      addNewCustomer,
      // Dashboard variables
      timeFilter,
      totalCustomers,
      newCustomers,
      newCustomersCount,
      activeCustomersCount,
      totalRevenue,
      applyTimeFilter,
      getStatusCount,
      getCustomerAvatar,
      getCustomerHandle,
      getStatusClass,
      // New modal properties and methods
      showAddModal,
      closeModal,
      newCustomer,
      saveCustomer,
      saving,
      // Delete modal properties and methods
      showDeleteModal,
      cancelDelete,
      confirmDelete,
      deleting,
      // View modal properties and methods
      showViewModal,
      closeViewModal,
      selectedCustomer,
      viewCustomer,
      // Edit modal properties and methods
      showEditModal,
      editingCustomer,
      editCustomer,
      cancelEdit,
      saveEdit,
      // Custom fields
      customFields,
      loadingCustomFields,
      customFieldValues,
      // Load More functionality
      loadingMore,
      displayLimit,
      totalCustomersCount,
      displayedCustomers,
      hasMoreCustomers,
      loadMoreCustomers,
      directT,
      // New functions for leads-style table
      handleSearch,
      selectedCustomers,
      toggleSelectAll,
      handlePageChange,
      getCustomerImageUrl,
      showAssignModal,
      showBulkAssignModal,
      getAssignedUserName,
      currentPage,
      itemsPerPage,
      // Filter modal variables and methods
      showFilterModal,
      filters,
      customerStatuses,
      customerSources,
      hasActiveFilters,
      activeFilterCount,
      closeFilterModal,
      toggleFilter,
      clearAllFilters,
      applyFilters
    };
  },
});
</script>

<style scoped>
/* Minimal modern styles - most styling is handled by Tailwind classes */
</style>
